import {useEffect} from 'react'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'
import {useLocation} from 'react-router-dom'

/**
 * Default UTM parameters object used when no UTM parameters are present.
 * @type {Object}
 * @property {string} utm_source - The source of the traffic (default: 'direct')
 * @property {string} utm_medium - The marketing medium (default: 'none')
 * @property {string} utm_campaign - The campaign name (default: 'none')
 * @property {string} utm_term - The paid search term (default: 'none')
 * @property {string} utm_content - The ad content description (default: 'none')
 */
const emptyUtmParams = {
    utm_source: 'direct',
    utm_medium: 'none',
    utm_campaign: 'none',
    utm_term: 'none',
    utm_content: 'none'
}

/**
 * Custom React hook that manages UTM parameters in localStorage based on the current URL.
 * - Checks for the presence of UTM parameters in the URL query string.
 * - If present, stores or appends them in localStorage, using default values if missing.
 * - Clears localStorage if parameters are expired.
 *
 * @returns {void}
 */
const useUTMParameters = () => {
    const location = useLocation()

    useEffect(() => {
        if (isServer) {
            return
        }

        // Call the function to clear localStorage if needed
        clearLocalStorageIfExpired()

        const urlParams = new URLSearchParams(location.search)
        const utmParams = Object.keys(emptyUtmParams)

        // Check if at least one UTM parameter is present in the URL
        const hasUtmParam = utmParams.some((param) => urlParams.has(param))

        if (hasUtmParam) {
            // Store all UTM parameters, using defaults if necessary
            utmParams.forEach((param) => {
                const newValue = urlParams.get(param) || emptyUtmParams[param]
                const existingValue = localStorage.getItem(param)

                if (existingValue) {
                    localStorage.setItem(param, `${existingValue}|${newValue}`)
                } else {
                    localStorage.setItem(param, newValue)
                }
            })
        }
    }, [location])
}

/**
 * Stores the current date and time as the last order date in localStorage.
 * The date is saved in ISO 8601 string format under the key 'lastOrderDate'.
 *
 * @returns {void}
 */
export const setLastOrderDate = () => {
    const currentDate = new Date().toISOString()
    localStorage.setItem('lastOrderDate', currentDate)
}

/**
 * Clears specific localStorage keys if the stored 'lastOrderDate' is older than 7 days.
 * Checks the 'lastOrderDate' in localStorage, calculates the difference in days from the current date,
 * and if more than 7 days have passed, removes all UTM parameter keys (from emptyUtmParams) and 'lastOrderDate' itself from localStorage.
 *
 * @returns {void}
 */
const clearLocalStorageIfExpired = () => {
    const lastOrderDate = localStorage.getItem('lastOrderDate')
    if (lastOrderDate) {
        const lastOrderDateObj = new Date(lastOrderDate)
        const currentDateObj = new Date()
        const diffInDays = (currentDateObj - lastOrderDateObj) / (1000 * 60 * 60 * 24)

        if (diffInDays > 7) {
            // List of keys to remove from localStorage
            const keysToRemove = [...Object.keys(emptyUtmParams), 'lastOrderDate']
            keysToRemove.forEach((key) => localStorage.removeItem(key))
        }
    }
}

/**
 * Retrieves a value from localStorage by key, decodes it, and limits its length to 200 characters.
 * @param {string} key - The key used to retrieve the value from localStorage.
 * @returns {string} The decoded value from localStorage, truncated to 200 characters if necessary, or an empty string if not found.
 */
export const getLimitedLocalStorageValue = (key) => {
    const value = localStorage.getItem(key)
    if (value) {
        const decodedValue = decodeURIComponent(value)
        return decodedValue.length > 200 ? decodedValue.substring(0, 200) : decodedValue
    }
    return ''
}

/**
 * Retrieves all standard UTM parameters from local storage and returns them as an object.
 * @returns {Object} An object containing the values of 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', and 'utm_content' from local storage.
 */
export const getAllUTMParameters = () => {
    const utmParams = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']
    const utmObject = {}

    utmParams.forEach((param) => {
        utmObject[param] = getLimitedLocalStorageValue(param)
    })

    return utmObject
}

/**
 * Exports the default hook for handling UTM parameters in the application.
 * @function
 * @name useUTMParameters
 * @exports
 * @returns {Object} - An object containing parsed UTM parameters from the URL.
 */
export default useUTMParameters
